import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  ScanPaymentProvider,
  SiteContext,
  ThemeMarketItem,
  ThemeMarketView,
} from '@icetus/contracts';
import { PAYMENT_PROVIDER_LABEL } from '@icetus/contracts';
import { Badge, Button, Card, formatMoney } from '@icetus/ui';
import { themeSummaries, type ThemeSummary } from '@icetus/theme-kit';
import {
  ApiError,
  createThemeOrder,
  fetchSites,
  fetchThemeCatalog,
  fetchThemeMarket,
  mockPayThemeOrder,
  saveSiteSettings,
  setThemeCancel,
} from '../api';
import { ThemeThumb } from '../components/theme-thumb';
import { ThemePublishPanel } from './theme-publish-panel';

/**
 * 主题市场。
 *
 * 这个页面只讲一件事：**哪些主题是你的，哪些要另外买，买完多久到期**。
 * 不做价格对比表 —— 主题之间不是"配置高低"的关系，是审美偏好，
 * 摆一张参数表只会让客户更选不出来。
 *
 * 三条实现取舍：
 *
 *   1) **付完不是"前端说我付了"**，而是重新拉一次市场数据，
 *      订阅状态一律以服务端为准。前端据此画绿标，就等于把开通权限交给浏览器。
 *   2) **退订是期末生效**，页面上必须写清"用到哪天"，否则客户会以为点了就没了。
 *   3) 基础三套永远不在这里出现"免费"以外的状态 —— 它们不受任何订阅影响，
 *      给它们加锁等于把退订的客户关在门外。
 */

/**
 * 付费主题是否对外开放。
 *
 * **暂时关着**：截至 2026-11，市场上没有任何一套在售的付费主题，那一整张
 * 「付费主题」卡片只会反复告诉客户"现在没有"，还顺带把订阅到期、宽限期、
 * 退订这些暂时用不到的规则讲一遍。等真正决定开放付费时，把这里改成 true
 * 即可 —— 卡片、订阅按钮、付款方式选择、页脚那段规则说明全都还在。
 *
 * 刻意用代码常量而不是接口开关：这是一次性的形态调整，不是按租户/按时间的
 * 灰度。做成接口开关意味着要动契约、迁移、后台表单，为了一个"暂不展示"不值。
 */
const PAID_THEMES_ENABLED = false;

const REASON_HINT: Record<ThemeMarketItem['reason'], string> = {
  free: '包含在套餐内，随时可切换',
  subscribed: '订阅中',
  grace: '已到期，宽限期内仍可用',
  expired: '订阅已到期',
  never: '需要单独订阅',
};

function yuan(cents: number): string {
  return `¥${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function ThemeMarketPage({
  tenantId,
  platformAdmin = false,
  onBack,
}: {
  tenantId: string;
  /**
   * 平台超管才显示"发布主题"入口。
   *
   * 只控制**看不看得见**：接口在 `/v1/admin/themes` 上另有一道
   * `PlatformAdminGuard`，改前端状态变量绕不过去。
   */
  platformAdmin?: boolean;
  /**
   * 回「站点信息」。
   *
   * 这一页已经从侧栏撤了入口（换主题的入口并进了「站点信息 → 外观」），
   * 客户是从那里点进来的。不留一条回去的路，他就得去侧栏找「站点信息」——
   * 而侧栏里现在没有任何一项是亮的，看着像走岔了。
   */
  onBack?: () => void;
}) {
  const [view, setView] = useState<ThemeMarketView | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [showPublish, setShowPublish] = useState(false);
  const pollTimer = useRef<number | null>(null);
  /**
   * 正在看大图的那套主题。
   *
   * 卡片上的图只有一百多像素宽，客户看不出这套主题的版式与留白 ——
   * 而"这张图好不好看"正是他决定订阅的依据。所以点图看 1600×1000 的
   * 完整首屏，**并且应用也在这里完成**（见 `openDetail`）。
   */
  const [detailName, setDetailName] = useState<string | null>(null);
  /**
   * 「应用」这一步的状态。它跟着大图走，不再是一张独立的页面区块。
   *
   * 订阅完就地应用，而不是叫客户去站点设置里再找一遍：主题市场里点"订阅"的人，
   * 想的是"我的站要变成这样"，不是"我要去另一个页面完成第二步"。
   * 那一步唯一需要他选的是**哪个站点**（多站租户）和**首屏排版**。
   */
  const [applySiteId, setApplySiteId] = useState('');
  const [applyVariant, setApplyVariant] = useState('');
  const [applying, setApplying] = useState(false);
  /** 应用失败时的原因。弹层盖住页面通知，错误必须就地显示，否则等于没报 */
  const [applyError, setApplyError] = useState('');
  /** 应用成功后的一句话：留在弹层里，客户不用退出去确认有没有生效 */
  const [applyResult, setApplyResult] = useState('');
  /** 站点列表懒加载：只在真要应用时拉一次。null = 还没拉过 */
  const [sites, setSites] = useState<SiteContext[] | null>(null);

  /**
   * 缩略图与变体都来自**服务端注册表**的摘要。
   *
   * 本地 `themeSummaries()` 只有内置四套，后台发布的主题在控制台眼里不存在：
   * 卡片没缩略图、应用面板没有变体可挑。先用本地那四套铺首屏，接口回来再换成
   * 全集 —— 两次都是同一份结构，不会出现"先空一下再跳出来"。
   */
  const [catalog, setCatalog] = useState<ThemeSummary[]>(() => themeSummaries());
  useEffect(() => {
    let cancelled = false;
    fetchThemeCatalog(tenantId)
      .then((res) => {
        if (!cancelled) setCatalog(res.items);
      })
      .catch(() => {
        // 拿不到就继续用内置四套：卡片少个缩略图，不影响订阅与退订
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const summaries = useMemo(() => {
    const map = new Map<string, ThemeSummary>();
    for (const s of catalog) map.set(s.name, s);
    return map;
  }, [catalog]);

  const load = useCallback(async () => {
    try {
      const next = await fetchThemeMarket(tenantId);
      setView(next);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * 打开大图。应用要用的站点列表也在这里备好。
   *
   * **应用只有一个地方：这张大图里。** 早先是点"应用"关掉大图、在页面顶部
   * 变出一张表单卡片 —— 客户刚把注意力放在一张 1600px 的首屏上，点一下就被
   * 弹回铺满卡片的列表、还得自己往下找那张突然出现的面板。既然"点开大图"
   * 已经表达了"我看上这套了"，下一步就该在原地完成。
   *
   * 首屏排版默认落到该主题的第一个变体：与站点设置里换主题的行为一致。
   * 沿用上一套主题的变体名只会得到一次静默回退 —— 各主题的变体不是同一套东西。
   */
  const openDetail = useCallback(
    async (name: string): Promise<void> => {
      setDetailName(name);
      setApplyResult('');
      setApplyError('');
      setApplyVariant(summaries.get(name)?.defaultVariant ?? '');

      const pick = (items: SiteContext[]): void => {
        setApplySiteId((prev) =>
          items.some((s) => s.site.id === prev) ? prev : (items[0]?.site.id ?? ''),
        );
      };
      if (sites) {
        pick(sites);
        return;
      }
      try {
        const items = await fetchSites(tenantId);
        setSites(items);
        pick(items);
      } catch {
        // 拉不到就当"没有站点"，弹层里照实说一句，比一直转圈诚实
        setSites([]);
      }
    },
    [summaries, sites, tenantId],
  );

  /**
   * 应用主题 = 改一个站点设置字段。
   *
   * 走 `PATCH /v1/sites/:id/settings` 的局部更新，只带 appearance：
   * 服务端对未订阅的付费主题会返回 402，权益判定不在这层重写一遍。
   *
   * 成功后**不关闭弹层**：客户还要看着这张大图确认是不是他要的那套，
   * 结果就写在图上那句「已应用到…」里。
   */
  const onApply = async (item: ThemeMarketItem): Promise<void> => {
    if (!applySiteId) return;
    setApplying(true);
    setApplyError('');
    try {
      const appearance: { theme: string; variant?: string } = { theme: item.name };
      if (applyVariant) appearance.variant = applyVariant;
      await saveSiteSettings(tenantId, applySiteId, { appearance });
      const siteName = sites?.find((s) => s.site.id === applySiteId)?.site.name ?? '站点';
      setApplyResult(`已应用到「${siteName}」，前台刷新就能看到。`);
      setNotice(`「${item.labelZh}」已应用到「${siteName}」，前台刷新就能看到。`);
    } catch (err) {
      setApplyError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setApplying(false);
    }
  };

  const pending = view?.pendingOrder ?? null;
  /** 两个扫码通道都有时才让选；只有一个时交给服务端挑（见 billing-page 同名字段） */
  const bothScan = (view?.wechatEnabled ?? false) && (view?.alipayEnabled ?? false);
  const [payProvider, setPayProvider] = useState<ScanPaymentProvider>('wechat');

  /**
   * 待支付单在场时每 3 秒刷一次市场数据。
   *
   * 不轮询单笔订单接口：主题订单与套餐订单共用一张表，但视图不同
   * （那个接口按套餐渲染，主题单进去会 500）。整页重拉一次也只多几毫秒。
   */
  useEffect(() => {
    if (pollTimer.current !== null) {
      window.clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
    if (!pending || pending.status !== 'pending') return;
    /** 到账后要应用的就是这张单上的主题；闭包捕获，不能等 load() 回来再猜 */
    const paidName = pending.themeName;
    pollTimer.current = window.setInterval(() => {
      void fetchThemeMarket(tenantId)
        .then((next) => {
          const still = next.pendingOrder;
          if (!still || still.status !== 'pending') {
            setNotice('已到账。在这套主题的大图里选站点，立刻应用上去。');
            // 先刷完市场数据再开图：开图那一刻要按新的订阅状态决定
            // 显示"订阅"还是"应用到站点"，顺序反了会闪一下旧的按钮
            void load().then(() => openDetail(paidName));
          }
        })
        .catch(() => {
          // 轮询失败不打扰客户：二维码还在，下一轮会再试
        });
    }, 3000);
    return () => {
      if (pollTimer.current !== null) {
        window.clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [pending, tenantId, load, openDetail]);

  const onSubscribe = async (item: ThemeMarketItem): Promise<void> => {
    setBusy(item.name);
    setNotice('');
    try {
      await createThemeOrder(tenantId, item.name, bothScan ? payProvider : undefined);
      await load();
      setNotice(
        view?.wechatEnabled || view?.alipayEnabled
          ? '二维码已生成，用手机扫码支付。付完这个页面会自动刷新。'
          : '订单已创建。请联系我们完成对公转账，到账后即可开通。',
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  /**
   * 换一种付款方式 = 重新下一单。
   *
   * 服务端会先关掉旧的待支付单（复用条件里带了 provider），所以不会出现
   * 两张单同时挂着。**不能**把旧单改成另一个通道：单号已经报给过通道，
   * 改一个等于造一张对方不认识的单。
   */
  const onSwitchProvider = async (next: ScanPaymentProvider): Promise<void> => {
    if (!pending) return;
    setBusy('switch');
    try {
      await createThemeOrder(tenantId, pending.themeName, next);
      setPayProvider(next);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const onMockPay = async (): Promise<void> => {
    if (!pending) return;
    setBusy('mock');
    try {
      await mockPayThemeOrder(tenantId, pending.orderId);
      await load();
      setNotice('模拟支付完成（测试模式），订阅已生效。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const onCancel = async (item: ThemeMarketItem, cancel: boolean): Promise<void> => {
    setBusy(item.name);
    try {
      await setThemeCancel(tenantId, item.name, cancel);
      await load();
      setNotice(
        cancel
          ? `已提交退订，${item.subscription ? fmtDate(item.subscription.currentPeriodEnd) : '本周期结束'}后失效；期间仍可正常使用。`
          : '已撤销退订，到期后会自动续订。',
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  if (error && !view) {
    return (
      <section className="stack">
        {onBack ? (
          <button type="button" className="linkbtn market-back" onClick={onBack}>
            ← 返回站点信息
          </button>
        ) : null}
        <Card title="主题市场" description="读取订阅状态失败。">
          <p className="muted">{error}</p>
          <Button variant="ghost" onClick={() => void load()}>
            重试
          </Button>
        </Card>
        {/*
          市场读不出来的时候，超管更需要这个入口：那通常意味着后端有问题，
          而发布面板恰好是排查"主题库读不读得到"的地方。
        */}
        {platformAdmin ? <ThemePublishPanel /> : null}
      </section>
    );
  }

  if (!view) return <p className="muted">加载中…</p>;

  const free = view.items.filter((i) => !i.paid);
  const paid = view.items.filter((i) => i.paid);
  /**
   * 已订阅（含宽限期）的付费主题：单独置顶成一组。
   *
   * 它们原来是埋在一排付费卡里的，客户想"我现在用的是哪套、哪天到期"
   * 要在二十张卡里横向找绿标。置顶之后这个问题不再存在。
   * 宽限期的也算"已订阅" —— 它还在用，只是快到期了。
   */
  const subscribedItems = paid.filter((i) => i.reason === 'subscribed' || i.reason === 'grace');
  /** 还能下单的付费主题。排除已订阅的，否则同一套会在页面上出现两次 */
  const paidForSale = paid.filter((i) => i.reason !== 'subscribed' && i.reason !== 'grace');
  const cancelling = view.items.filter((i) => i.subscription?.cancelAtPeriodEnd);
  const inGrace = view.items.filter((i) => i.reason === 'grace');
  /** 正在看大图的那套。用名字而不是对象：刷新后市场数据是新对象，缓存对象会显示旧价格 */
  const detailItem = detailName ? (view.items.find((i) => i.name === detailName) ?? null) : null;
  /** 能不能直接应用：基础主题随便用，付费的要在订阅有效期内（宽限期也算） */
  const detailApplicable = detailItem
    ? !detailItem.paid || detailItem.reason === 'subscribed' || detailItem.reason === 'grace'
    : false;

  return (
    <section className="stack">
      {onBack ? (
        <button type="button" className="linkbtn market-back" onClick={onBack}>
          ← 返回站点信息
        </button>
      ) : null}
      {platformAdmin ? (
        <div className="market-admin-bar">
          <span className="muted">平台管理员可见</span>
          <Button variant="ghost" onClick={() => setShowPublish((v) => !v)}>
            {showPublish ? '收起发布面板' : '发布主题'}
          </Button>
        </div>
      ) : null}
      {platformAdmin && showPublish ? <ThemePublishPanel /> : null}

      {/*
        退订与宽限的提醒放在最上面：客户主动退订后最怕"哪天站突然变样"，
        而到期提醒本身就是这个页面存在的第二个理由。
      */}
      {cancelling.length > 0 ? (
        <div className="notice notice--warn">
          {cancelling.map((i) => i.subscription).map((s) => (
            <div key={s!.themeName}>
              「{(paid.find((p) => p.name === s!.themeName) ?? free.find((p) => p.name === s!.themeName))?.labelZh ?? s!.themeName}」
              已提交退订，<strong>{fmtDate(s!.currentPeriodEnd)}</strong> 到期后站点会回到基础主题。
              期间仍可正常使用，随时可以撤销。
            </div>
          ))}
        </div>
      ) : null}

      {inGrace.length > 0 ? (
        <div className="notice notice--danger">
          {inGrace.map((i) => (
            <div key={i.name}>
              「{i.labelZh}」订阅已到期，正在宽限期（还有 {Math.max(i.subscription?.daysLeft ?? 0, 0)} 天）。
              续订后一切不变；宽限期结束站点会回到基础主题。
            </div>
          ))}
        </div>
      ) : null}

      {notice ? <div className="notice notice--ok">{notice}</div> : null}
      {error && view ? <div className="notice notice--danger">{error}</div> : null}

      {/* 待支付单：扫码通道给二维码，人工收款给一段说明 */}
      {pending && pending.status === 'pending' ? (
        <Card
          title={`待支付 · ${pending.themeLabel}`}
          description={
            pending.provider !== 'manual'
              ? `用${PAYMENT_PROVIDER_LABEL[pending.provider]}扫码完成支付，付完本页会自动刷新。`
              : '当前未开通扫码支付。请联系我们完成对公转账，到账后开通。'
          }
        >
          <div className="market-pay">
            {pending.provider !== 'manual' && pending.qrSvg ? (
              // 二维码由服务端生成（链接里带商户单号，不交给第三方二维码服务）
              <div className="market-pay__qr" dangerouslySetInnerHTML={{ __html: pending.qrSvg }} />
            ) : (
              <div className="market-pay__manual">
                <p>
                  金额 <strong>{formatMoney(pending.amountCents, 'CNY', 'zh-CN')}</strong>，
                  订单号 <code>{pending.number}</code>
                </p>
                <p className="muted">转账备注里写上订单号，核对更快。</p>
              </div>
            )}
            <div className="market-pay__side">
              <p className="muted">
                金额 {formatMoney(pending.amountCents, 'CNY', 'zh-CN')} · 订单号 {pending.number}
              </p>
              {pending.secondsLeft !== null ? (
                <p className="muted">二维码约 {Math.ceil(pending.secondsLeft / 60)} 分钟内有效</p>
              ) : null}
              {view.mockPayEnabled && pending.provider === 'manual' ? (
                <Button disabled={busy === 'mock'} onClick={() => void onMockPay()}>
                  {busy === 'mock' ? '处理中…' : '模拟支付（测试）'}
                </Button>
              ) : null}
              {/*
                只有另一种通道确实开着才给这个按钮。摆在"没有那个通道"的时候，
                点下去只会得到一句"暂未开通"，比不显示更让人困惑。
              */}
              {pending.provider !== 'manual' &&
              ((pending.provider === 'wechat' && view.alipayEnabled) ||
                (pending.provider === 'alipay' && view.wechatEnabled)) ? (
                <Button
                  disabled={busy !== null}
                  onClick={() => void onSwitchProvider(pending.provider === 'wechat' ? 'alipay' : 'wechat')}
                >
                  {busy === 'switch'
                    ? '切换中…'
                    : `改用${PAYMENT_PROVIDER_LABEL[pending.provider === 'wechat' ? 'alipay' : 'wechat']}付款`}
                </Button>
              ) : null}
            </div>
          </div>
        </Card>
      ) : null}

      {/*
        已订阅置顶。
        这里**只放缩略图**，不展开首屏大图：客户走到这一段是来管理已有的
        几套主题（看看到期日、换个站应用、或者退订），一屏被一张大图占满
        反而要一直往下滚；想确认版式，点缩略图看大图，应用也在那张图里。
      */}
      {subscribedItems.length > 0 ? (
        <Card
          title="我的订阅"
          description="点缩略图看完整首屏，并把它应用到某个站点。到期前都能正常使用，退订在当期结束时生效。"
        >
          <div className="theme-grid theme-grid--tile">
            {subscribedItems.map((item) => {
              const sub = item.subscription;
              const graced = item.reason === 'grace';
              return (
                <MarketCard
                  key={item.name}
                  item={item}
                  summary={summaries.get(item.name)}
                  onOpen={() => void openDetail(item.name)}
                  badge={
                    <Badge tone={graced ? 'warn' : 'success'}>{graced ? '宽限中' : '订阅中'}</Badge>
                  }
                  meta={`${yuan(item.monthlyCents)}/月`}
                  state={
                    sub
                      ? graced
                        ? `已到期 · 宽限 ${Math.max(sub.daysLeft ?? 0, 0)} 天`
                        : sub.cancelAtPeriodEnd
                          ? `已退订 · 用到 ${fmtDate(sub.currentPeriodEnd)}`
                          : `到期 ${fmtDate(sub.currentPeriodEnd)}`
                      : REASON_HINT[item.reason]
                  }
                >
                  {sub?.cancelAtPeriodEnd ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy === item.name}
                      onClick={() => void onCancel(item, false)}
                    >
                      撤销退订
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy === item.name}
                      onClick={() => void onCancel(item, true)}
                    >
                      退订
                    </Button>
                  )}
                </MarketCard>
              );
            })}
          </div>
        </Card>
      ) : null}

      {/*
        付费主题整块按 `PAID_THEMES_ENABLED` 开关显示。关着的时候连"暂无在售"
        那句空态也一起收起 —— 一整张卡只为说"现在没有东西卖"，不如不出现。
        已有的订阅（`我的订阅`）与到期提醒**不受开关影响**：开关关的是"卖"，
        不是"已经在用的那几套"。
      */}
      {PAID_THEMES_ENABLED ? (
        <Card
          title={subscribedItems.length > 0 ? '更多付费主题' : '付费主题'}
          description="按套、按月单独订阅，不包含在套餐里。订阅到期后站点自动回到下面的基础主题，内容、图片、文字都不受影响。"
        >
          <div className="theme-grid theme-grid--tile">
            {bothScan && paidForSale.length > 0 ? (
              <div className="pay-method">
                <span className="pay-method__label">付款方式</span>
                <div className="segmented">
                  {(['wechat', 'alipay'] as const).map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`segmented__item${payProvider === p ? ' is-active' : ''}`}
                      onClick={() => setPayProvider(p)}
                    >
                      {PAYMENT_PROVIDER_LABEL[p]}
                    </button>
                  ))}
                </div>
                <span className="muted">扫码付款，付完自动开通</span>
              </div>
            ) : null}
            {paidForSale.map((item) => (
              <MarketCard
                key={item.name}
                item={item}
                summary={summaries.get(item.name)}
                onOpen={() => void openDetail(item.name)}
                meta={`${yuan(item.monthlyCents)}/月`}
                state={REASON_HINT[item.reason]}
              >
                <Button
                  size="sm"
                  disabled={busy === item.name}
                  onClick={() => void onSubscribe(item)}
                >
                  {busy === item.name ? '处理中…' : '订阅'}
                </Button>
              </MarketCard>
            ))}
            {paidForSale.length === 0 ? (
              <p className="muted market-empty">
                {paid.length === 0
                  ? '目前在售的付费主题为零 —— 下面的基础三套可以直接应用，不受任何订阅影响。有新主题上架后会出现在这里。'
                  : '在售的付费主题都已订阅，在上面的「我的订阅」里管理。'}
              </p>
            ) : null}
          </div>
          {paid.length > 0 ? (
            <p className="muted market-foot">
              主题是订阅制，<strong>不退当期费用</strong>：退订后用到本期结束，到期不再续。
              重新订阅时上次的选择还在，不用重新挑一遍。年付还在做，目前只支持按月。
            </p>
          ) : null}
        </Card>
      ) : null}

      {/*
        基础主题那张卡的说明跟着开关走：付费主题不展示时，"到期后回到这里"
        指向的是页面上根本不存在的东西，客户会去找那张并不存在的卡。
      */}
      <Card
        title="基础主题"
        description={
          PAID_THEMES_ENABLED
            ? '这三套包含在套餐里，不受任何订阅影响。点开图就能应用到站点，付费主题到期后站点会回到这里。'
            : '这三套包含在套餐里，不受任何订阅影响。点开图就能应用到站点。'
        }
      >
        <div className="theme-grid theme-grid--tile">
          {free.map((item) => (
            <MarketCard
              key={item.name}
              item={item}
              summary={summaries.get(item.name)}
              onOpen={() => void openDetail(item.name)}
              badge={<Badge tone="neutral">已包含</Badge>}
              meta={`${item.variantCount} 种变体 · v${item.version}`}
            />
          ))}
        </div>
      </Card>

      {detailItem ? (
        <div
          className="theme-detail"
          role="dialog"
          aria-modal="true"
          aria-label={`${detailItem.labelZh} 预览`}
          onClick={() => setDetailName(null)}
        >
          <div className="theme-detail__panel" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="theme-detail__close"
              aria-label="关闭"
              onClick={() => setDetailName(null)}
            >
              ×
            </button>
            {detailItem.previewHeroUrl || detailItem.previewCardUrl ? (
              <img
                className="theme-detail__hero"
                src={(detailItem.previewHeroUrl ?? detailItem.previewCardUrl)!}
                alt={`${detailItem.labelZh} 首屏预览`}
              />
            ) : (
              <div className="theme-detail__nohero muted">
                这套主题还没上传预览图，下面是它的配色示意
                {(() => {
                  const s = summaries.get(detailItem.name);
                  return s ? <ThemeThumb theme={s} /> : null;
                })()}
              </div>
            )}
            <div className="theme-detail__body">
              <h3 className="theme-detail__title">
                {detailItem.labelZh}
                <span className="muted"> {detailItem.labelEn}</span>
              </h3>
              <p className="theme-detail__desc">
                {detailItem.descriptionZh || detailItem.descriptionEn}
              </p>
              <p className="muted">
                {detailItem.paid ? `${yuan(detailItem.monthlyCents)}/月` : '包含在套餐里'} ·{' '}
                {detailItem.variantCount} 种变体
                {detailItem.serifHeading ? ' · 衬线标题' : ''} · v{detailItem.version}
              </p>

              {/*
                应用就在这张图里做完。
                没订阅的付费主题先给"订阅"，付完款轮询到账后会重新打开这张图，
                那时按钮变成下面的站点/排版选择 —— 客户不用回市场页找面板。
              */}
              {detailApplicable ? (
                applyResult ? (
                  <div className="theme-detail__done">
                    <p className="theme-detail__ok">{applyResult}</p>
                    <div className="chips">
                      <Button variant="ghost" onClick={() => setDetailName(null)}>
                        关闭
                      </Button>
                    </div>
                  </div>
                ) : sites === null ? (
                  <p className="muted">读取站点…</p>
                ) : sites.length === 0 ? (
                  <p className="muted">这个账号下还没有站点。先去「站点」建一个，再回来应用。</p>
                ) : (
                  <div className="theme-detail__apply">
                    <p className="muted theme-detail__hint">
                      只换外观：骨架、字体、配色与圆角。页面内容、图片、文案一个字都不动，前台刷新即可看到。
                    </p>
                    {sites.length > 1 ? (
                      <div className="form-row theme-detail__row">
                        <span>站点</span>
                        <div className="chips">
                          {sites.map((s) => (
                            <button
                              key={s.site.id}
                              type="button"
                              className={`pill${applySiteId === s.site.id ? ' pill--on' : ''}`}
                              aria-pressed={applySiteId === s.site.id}
                              onClick={() => setApplySiteId(s.site.id)}
                            >
                              {s.site.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="muted theme-detail__hint">站点：{sites[0]!.site.name}</p>
                    )}
                    {(summaries.get(detailItem.name)?.variants ?? []).length > 1 ? (
                      <div className="form-row theme-detail__row">
                        <span>首屏排版</span>
                        <div className="chips">
                          {(summaries.get(detailItem.name)?.variants ?? []).map((v) => (
                            <button
                              key={v.key}
                              type="button"
                              className={`pill${applyVariant === v.key ? ' pill--on' : ''}`}
                              aria-pressed={applyVariant === v.key}
                              onClick={() => setApplyVariant(v.key)}
                            >
                              {v.label.zh}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {applyError ? <p className="theme-detail__err">{applyError}</p> : null}
                    <div className="theme-detail__actions">
                      <Button
                        disabled={applying || !applySiteId}
                        onClick={() => void onApply(detailItem)}
                      >
                        {applying ? '应用中…' : '应用并生效'}
                      </Button>
                      <Button variant="ghost" onClick={() => setDetailName(null)}>
                        关闭
                      </Button>
                    </div>
                  </div>
                )
              ) : (
                <div className="theme-detail__apply">
                  <p className="muted theme-detail__hint">
                    订阅后就能把这套主题应用到站点。付完款这张图会自动重新打开，接着选站点。
                  </p>
                  <div className="theme-detail__actions">
                    <Button
                      disabled={busy === detailItem.name}
                      onClick={() => {
                        // 付款是另一条流程（二维码在页面顶部），所以这里关掉图
                        const target = detailItem;
                        setDetailName(null);
                        void onSubscribe(target);
                      }}
                    >
                      {busy === detailItem.name
                        ? '处理中…'
                        : `${yuan(detailItem.monthlyCents)}/月 订阅`}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/**
 * 一张市场卡片（窄版）。
 *
 * 三组（我的订阅 / 其它付费主题 / 基础主题）用的是同一张卡：
 * 差别只在徽标、状态一行、以及底部动作，所以拆出来。
 * 早先是三段各写一遍 `div.theme-card`，改一次布局要改三处。
 *
 * 卡片本身不是按钮（里面还有"订阅 / 退订"这种不可撤销的动作），
 * 只有缩略图可点 —— 点开看 1600×1000 的完整首屏，**应用也在那里做**。
 * 基础主题没有任何底部动作，卡片就只剩一张图：应用是点开图之后的事。
 */
function MarketCard(props: {
  item: ThemeMarketItem;
  summary: ThemeSummary | undefined;
  badge?: React.ReactNode;
  /** 价格 / 变体数这一行 */
  meta: string;
  /** 状态一行（订阅中 · 到期 2026-11-03）。基础主题没有就是空的 */
  state?: string;
  onOpen: () => void;
  /** 底部动作。不给就不渲染那一行（零动作的卡片不该留一条空白） */
  children?: React.ReactNode;
}): React.JSX.Element {
  const { item, summary, badge, meta, state, onOpen, children } = props;
  return (
    <article
      className="theme-card theme-card--static theme-card--tile"
      /* 描述在窄卡里放不下，挂到 title 上：不占版面，悬停仍然看得到 */
      title={item.descriptionZh || item.descriptionEn}
    >
      <CardPreview item={item} summary={summary} onOpen={onOpen} />
      <span className="theme-card__name theme-card__name--tile">
        <span className="theme-card__title">{item.labelZh}</span>
        {badge}
      </span>
      <span className="theme-card__meta">{meta}</span>
      {state ? <span className="muted market-card__state">{state}</span> : null}
      {children ? <div className="market-card__actions">{children}</div> : null}
    </article>
  );
}

/**
 * 卡片上的预览图。
 *
 * 有真图就用真图：客户比的是"这套主题长什么样"，而配色示意图只能回答
 * "这套是冷色还是暖色"。没图的（内置四套、以及还没传图的自发布主题）
 * 落回 `ThemeThumb` —— 它按令牌现画，比一块空白有用得多。
 *
 * 三种形态**都包在按钮里**，且哪怕一张图都拿不到也要给个占位：
 * 点开大图现在是进"应用"的唯一入口，图裂了就等于这套主题没法用。
 */
function CardPreview(props: {
  item: ThemeMarketItem;
  summary: ThemeSummary | undefined;
  onOpen: () => void;
}): React.JSX.Element {
  const { item, summary, onOpen } = props;
  return (
    <button
      type="button"
      className={`theme-card__preview${item.previewCardUrl ? '' : ' theme-card__preview--thumb'}`}
      title="看完整首屏"
      onClick={onOpen}
    >
      {item.previewCardUrl ? (
        /* lazy：市场一屏可能有二十张图，不 lazy 会把首屏带宽全吃掉 */
        <img src={item.previewCardUrl} alt={`${item.labelZh} 预览`} loading="lazy" />
      ) : summary ? (
        <ThemeThumb theme={summary} />
      ) : (
        <span className="theme-card__nofig muted">暂无预览图</span>
      )}
    </button>
  );
}
